<template>
  <!-- TKP-234: yayımlanan metin ile güncel envanterden üretilecek metin yan yana; farklar vurgulu, paneller eş zamanlı kayar -->
  <div>
    <div class="d-flex flex-wrap align-items-center mb-2">
      <template v-if="hasData === false"></template>
      <span v-else-if="result.hunks === 0" class="text-muted mr-3" data-test="diff-none">{{ $t("legal-document.diff-none") }}</span>
      <span v-else class="mr-3" data-test="diff-summary">
        {{ $t("legal-document.diff-summary", { hunks: result.hunks, inserted: result.inserted, deleted: result.deleted }) }}
      </span>
      <span class="small mr-3">
        <mark class="diff-del px-1">{{ $t("legal-document.diff-legend-del") }}</mark>
        <mark class="diff-ins px-1 ml-1">{{ $t("legal-document.diff-legend-ins") }}</mark>
      </span>
      <div v-if="result.hunks > 0" class="ml-auto">
        <b-button size="sm" variant="light" data-test="diff-prev" :disabled="active <= 0" @click="go(active - 1)">
          <i class="bx bx-chevron-up"></i> {{ $t("legal-document.diff-prev") }}
        </b-button>
        <span class="mx-2 small text-muted" data-test="diff-position">{{ active + 1 }} / {{ result.hunks }}</span>
        <b-button size="sm" variant="light" data-test="diff-next" :disabled="active >= result.hunks - 1" @click="go(active + 1)">
          {{ $t("legal-document.diff-next") }} <i class="bx bx-chevron-down"></i>
        </b-button>
      </div>
    </div>
    <div v-if="result.truncated" class="alert alert-info py-1 small" data-test="diff-truncated">{{ $t("legal-document.diff-truncated") }}</div>
    <div class="row">
      <div class="col-md-6">
        <h6 class="text-muted">{{ $t("legal-document.diff-stored") }}</h6>
        <div ref="stored" class="legal-diff border rounded p-2" data-test="diff-stored" @scroll="sync('stored')" v-html="result.stored"></div>
      </div>
      <div class="col-md-6">
        <h6 class="text-muted">{{ $t("legal-document.diff-current") }}</h6>
        <div v-if="hasData === false" class="alert alert-warning" data-test="diff-empty">{{ $t("legal-document.diff-empty") }}</div>
        <div v-else ref="current" class="legal-diff border rounded p-2" data-test="diff-current" @scroll="sync('current')" v-html="result.current"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { diffHtml } from "../text-diff";

/** Programla kaydırılan panelin kendi scroll olayı bu kadar ms yok sayılır (karşılıklı tetiklemeyi keser). */
const MUTE_MS = 80;

export default {
  name: "LegalDocumentDiff",
  props: {
    stored: { type: String, default: "" },
    current: { type: String, default: "" },
    hasData: { type: Boolean, default: null },
  },
  data() {
    return { active: -1, muted: { stored: 0, current: 0 } };
  },
  computed: {
    /** Güncel metin yoksa (eşleşen kayıt kalmamış) karşılaştırma yapılmaz, yayımlanan metin olduğu gibi gösterilir. */
    result() {
      if (this.hasData === false) return { stored: this.stored, current: "", inserted: 0, deleted: 0, hunks: 0, truncated: false };
      return diffHtml(this.stored, this.current);
    },
  },
  watch: {
    result() {
      this.active = -1;
    },
  },
  methods: {
    /** Bir panel kaydırılınca diğeri aynı ORANDA kayar; metin uzunlukları farklı olabilir. */
    sync(from) {
      const to = from === "stored" ? "current" : "stored";
      if (Date.now() < this.muted[from]) return;
      const src = this.$refs[from];
      const dst = this.$refs[to];
      if (!src || !dst) return;
      const srcRange = src.scrollHeight - src.clientHeight;
      const dstRange = dst.scrollHeight - dst.clientHeight;
      const target = srcRange > 0 ? (src.scrollTop / srcRange) * dstRange : 0;
      if (Math.abs(dst.scrollTop - target) < 1) return;
      this.muted[to] = Date.now() + MUTE_MS;
      dst.scrollTop = target;
    },
    /** k. farka git: her panel kendi işaretine kayar (yalnız eklemede soldaki, yalnız silmede sağdaki eşlenik oranla). */
    go(k) {
      if (k < 0 || k >= this.result.hunks) return;
      this.active = k;
      const panels = ["stored", "current"].map((side) => ({ side, el: this.$refs[side] })).filter((p) => p.el);
      let anchored = null;
      for (const p of panels) {
        p.el.querySelectorAll("mark.diff-active").forEach((m) => m.classList.remove("diff-active"));
        const marks = p.el.querySelectorAll(`mark[data-hunk="${k}"]`);
        marks.forEach((m) => m.classList.add("diff-active"));
        if (!marks.length) continue;
        this.muted[p.side] = Date.now() + MUTE_MS;
        p.el.scrollTop += marks[0].getBoundingClientRect().top - p.el.getBoundingClientRect().top - p.el.clientHeight / 3;
        anchored = anchored || p.side;
      }
      const other = panels.find((p) => !p.el.querySelector(`mark[data-hunk="${k}"]`));
      if (other && anchored) {
        this.muted[anchored] = 0;
        this.sync(anchored);
      }
    },
  },
};
</script>

<style scoped>
/* TKP-93: fark penceresinde iki metin yan yana ve uzunsa kendi içinde kayar */
.legal-diff {
  max-height: 60vh;
  overflow-y: auto;
  font-size: 0.85rem;
}
.diff-del,
.legal-diff ::v-deep mark.diff-del {
  background: #fde2e1;
  color: #a4161a;
  text-decoration: line-through;
  padding: 0 1px;
}
.diff-ins,
.legal-diff ::v-deep mark.diff-ins {
  background: #d3f4dc;
  color: #14632e;
  padding: 0 1px;
}
.legal-diff ::v-deep mark.diff-active {
  outline: 2px solid #f1734f;
}
</style>
